import React from 'react'
import PropTypes from 'prop-types'
import { Card, FlowGrid } from '@fs/zion-ui'
import ReviewCardHeader from './components/ReviewCardHeader'
import ReviewCardBody from './components/ReviewCardBody'
import ReviewCardFooter from './components/ReviewCardFooter'

/**
 * ReviewCard
 *
 * The Review Card is the final step on the Add Name InfoSheet/Panel. It prompts the user
 * to verify entered information and guides them to either continue adding details for
 * additional people or proceed to attach the record.
 *
 * Display Modes:
 *  - Pending (active=false): Shows only header with gray icon and border card.
 *  - Active (active=true): Shows header + body + footer with gray icon and elevated card.
 *
 * Footer Variants:
 *  - If familyMembersToReview array has items: Shows "Save and Continue" button.
 *  - If no familyMembersToReview: Shows "Attach to Tree" + optional "Add Household Member" + "Close" buttons.
 *
 * @component
 * @param {Object}   props
 * @param {boolean}  [props.active=true]        Controls visibility of body and footer. If false, only shows header.
 * @param {Object}   props.person               SLS person object containing at least localizedName.
 * @param {Array<Object>}    [props.familyMembersToReview=[]]   Array of new people objects (containing id, name) that need to have their details added.
 * @param {Function} props.onSave               Callback fired when user clicks a save button. Receives action type: 'close', 'closeAll', 'attach', or 'continue'.
 * @param {Function} props.onAddAnotherPerson   Callback fired when user clicks "Add Household Member".
 *
 * @returns {JSX.Element}
 */
export const ReviewCard = ({
  active = true,
  person,
  familyMembersToReview = [],
  onSave,
  onAddAnotherPerson,
  splitScreenPartitionKey,
}) => {
  if (!onSave) {
    console.error('ReviewCard: onSave prop is required but was not provided')
    return null
  }

  return (
    <Card margins="xxs" edges={active ? 'elevation' : 'border'}>
      <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
        <ReviewCardHeader active={active} />
        {active && (
          <ReviewCardBody
            person={person}
            familyMembersToReview={familyMembersToReview}
            splitScreenPartitionKey={splitScreenPartitionKey}
          />
        )}
        {active && (
          <ReviewCardFooter
            familyMembersToReview={familyMembersToReview}
            onSave={onSave}
            onAddAnotherPerson={onAddAnotherPerson}
          />
        )}
      </FlowGrid>
    </Card>
  )
}

ReviewCard.propTypes = {
  active: PropTypes.bool,
  person: PropTypes.shape({
    localizedName: PropTypes.string,
  }).isRequired,
  familyMembersToReview: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      localizedName: PropTypes.string.isRequired,
    })
  ),
  onSave: PropTypes.func.isRequired,
  onAddAnotherPerson: PropTypes.func.isRequired,
  splitScreenPartitionKey: PropTypes.string,
}

ReviewCard.defaultProps = {
  active: true,
  familyMembersToReview: [],
}

export default ReviewCard
